import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Mail,
  Eye,
  MousePointerClick,
  Percent,
  TrendingUp,
  Users,
  BarChart2,
  Download,
  RefreshCw,
  Loader2
} from "lucide-react";

// Summary Cards Component
export const AnalyticsSummaryCards = ({ analytics }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">Email Inviate</p>
            <p className="text-3xl font-bold">{analytics?.summary?.total_sent || 0}</p>
          </div>
          <Mail className="h-8 w-8 text-orange-500 opacity-50" />
        </div>
      </CardContent>
    </Card>
    
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">Aperture Uniche</p>
            <p className="text-3xl font-bold text-green-600">{analytics?.summary?.unique_opens || 0}</p>
            <p className="text-xs text-slate-400">{analytics?.summary?.open_rate || 0}% open rate</p>
          </div>
          <Eye className="h-8 w-8 text-green-500 opacity-50" />
        </div>
      </CardContent>
    </Card>
    
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">Click Totali</p>
            <p className="text-3xl font-bold text-blue-600">{analytics?.summary?.total_clicks || 0}</p>
            <p className="text-xs text-slate-400">{analytics?.summary?.click_rate || 0}% click rate</p>
          </div>
          <MousePointerClick className="h-8 w-8 text-blue-500 opacity-50" />
        </div>
      </CardContent>
    </Card>
    
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500">Click-to-Open</p>
            <p className="text-3xl font-bold text-purple-600">{analytics?.summary?.click_to_open_rate || 0}%</p>
            <p className="text-xs text-slate-400">dei lettori</p>
          </div>
          <Percent className="h-8 w-8 text-purple-500 opacity-50" />
        </div>
      </CardContent>
    </Card>
  </div>
);

// Click by Link Type Component
export const ClicksByTypeCard = ({ clicksByType }) => {
  if (!clicksByType) return null;
  
  const linkTypes = [
    { key: 'video', label: 'Video' },
    { key: 'consulenza', label: 'Consulenza' },
    { key: 'risorsa', label: 'Risorsa' },
    { key: 'chiamami', label: 'Chiamami' }
  ];
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MousePointerClick className="h-5 w-5" />
          Click per Tipo di Link
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {linkTypes.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <MousePointerClick className="h-5 w-5 text-slate-400" />
              <div>
                <p className="font-semibold">{clicksByType[key] || 0}</p>
                <p className="text-xs text-slate-500">{label}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Timeline Chart Component
export const TimelineChart = ({ timeline }) => {
  if (!timeline || timeline.length === 0) return null;
  
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Andamento Ultimi 30 Giorni
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48 flex items-end gap-1">
          {timeline.map((day, idx) => {
            const maxVal = Math.max(...timeline.map(d => Math.max(d.opens || 0, d.clicks || 0, d.sent || 0)), 1);
            const openHeight = ((day.opens || 0) / maxVal) * 100;
            const clickHeight = ((day.clicks || 0) / maxVal) * 100;
            
            return (
              <div 
                key={idx} 
                className="flex-1 flex flex-col items-center gap-1" 
                title={`${day.date}: ${day.opens || 0} aperture, ${day.clicks || 0} click`}
              >
                <div className="w-full flex gap-0.5 items-end h-40">
                  <div 
                    className="flex-1 bg-green-400 rounded-t transition-all"
                    style={{ height: `${openHeight}%`, minHeight: day.opens > 0 ? '4px' : '0' }}
                  />
                  <div 
                    className="flex-1 bg-blue-400 rounded-t transition-all"
                    style={{ height: `${clickHeight}%`, minHeight: day.clicks > 0 ? '4px' : '0' }}
                  />
                </div>
                {idx % 5 === 0 && (
                  <span className="text-[10px] text-slate-400">{day.date.slice(5)}</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-400 rounded" />
            <span className="text-sm text-slate-600">Aperture</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-400 rounded" />
            <span className="text-sm text-slate-600">Click</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Details Table Component
export const AnalyticsDetailsTable = ({ 
  data, 
  isLoading, 
  onRefresh, 
  onExport,
  entityName = "Lead" // "Lead" or "Membro"
}) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between">
      <CardTitle className="flex items-center gap-2">
        <Users className="h-5 w-5" />
        Dettagli per {entityName}
      </CardTitle>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Aggiorna
        </Button>
        <Button variant="outline" size="sm" onClick={onExport} disabled={!data?.length}>
          <Download className="h-4 w-4 mr-2" />
          Esporta CSV
        </Button>
      </div>
    </CardHeader>
    <CardContent>
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : !data?.length ? (
        <div className="text-center py-12 text-slate-500">
          <BarChart2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
          <p>Nessun dato di tracking disponibile</p>
          <p className="text-sm mt-2">Invia email con tracking attivo per vedere le statistiche</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{entityName}</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Data Invio</TableHead>
                <TableHead className="text-center">Aperture</TableHead>
                <TableHead>Ultima Apertura</TableHead>
                <TableHead className="text-center">Click</TableHead>
                <TableHead>Ultimo Click</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">
                    {item.lead_name || item.member_name || '-'}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {item.lead_email || item.member_email}
                  </TableCell>
                  <TableCell className="text-sm">
                    {item.sent_at 
                      ? new Date(item.sent_at).toLocaleDateString('it-IT', { 
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                        }) 
                      : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    {item.open_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                        <Eye className="h-3 w-3" />
                        {item.open_count}
                      </span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {item.last_opened 
                      ? new Date(item.last_opened).toLocaleDateString('it-IT', { 
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                        }) 
                      : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    {item.total_clicks > 0 ? (
                      <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                        <MousePointerClick className="h-3 w-3" />
                        {item.total_clicks}
                      </span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {item.last_clicked 
                      ? new Date(item.last_clicked).toLocaleDateString('it-IT', { 
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' 
                        }) 
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </CardContent>
  </Card>
);

// Full Analytics Dashboard Component
const AnalyticsDashboard = ({
  analytics,
  isLoading,
  onRefresh,
  onExport,
  entityName = "Lead"
}) => (
  <div className="space-y-6">
    <AnalyticsSummaryCards analytics={analytics} />
    <ClicksByTypeCard clicksByType={analytics?.clicks_by_type} />
    <TimelineChart timeline={analytics?.timeline} />
    <AnalyticsDetailsTable
      data={analytics?.leads || analytics?.members}
      isLoading={isLoading}
      onRefresh={onRefresh}
      onExport={onExport}
      entityName={entityName}
    />
  </div>
);

export default AnalyticsDashboard;
